import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Bell, User, Settings, LogOut, ChevronDown, 
  Sun, Moon 
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext'; // Opsiyonel: Tema Context yoksa hata vermez
import { languages } from '../translations/translations';

const HeaderActions = () => {
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  
  // Tema Kontrolü (Güvenli Erişim)
  const themeContext = useTheme ? useTheme() : { theme: 'dark', toggleTheme: () => {} };
  const { toggleTheme } = themeContext;

  const currentLang = languages.find(l => l.code === language) || languages[0];

  return (
    <div className="flex items-center gap-2 sm:gap-3">
      
      {/* 1. THEME TOGGLE (ANIMATED) */}
      <Button 
        onClick={toggleTheme} 
        variant="ghost" 
        className="w-10 h-10 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 text-slate-300 hover:text-white transition-all active:scale-95 relative overflow-hidden group"
      >
        <div className="relative w-5 h-5">
            <Sun 
                size={20} 
                className="absolute inset-0 transition-all duration-500 rotate-0 scale-100 dark:-rotate-90 dark:scale-0 text-orange-400" 
            />
            <Moon 
                size={20} 
                className="absolute inset-0 transition-all duration-500 rotate-90 scale-0 dark:rotate-0 dark:scale-100 text-blue-400" 
            />
        </div>
        <div className="absolute inset-0 bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl" />
      </Button>

      {/* 2. LANGUAGE SELECTOR */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-10 px-3 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 text-white gap-2 transition-all active:scale-95 min-w-[70px] justify-between">
            <span className="text-lg leading-none filter drop-shadow-md">{currentLang.flag}</span>
            <span className="text-[10px] font-black uppercase tracking-widest hidden sm:inline-block text-slate-200">{currentLang.code}</span>
            <ChevronDown size={12} className="opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="bg-[#111827]/95 backdrop-blur-xl border-white/10 text-white rounded-xl w-40 shadow-2xl shadow-black/50 p-1">
          <DropdownMenuLabel className="text-[9px] font-black text-slate-500 uppercase tracking-widest px-3 py-2">
            {t('setting_language') || 'Language'}
          </DropdownMenuLabel>
          <DropdownMenuSeparator className="bg-white/10" />
          <div className="max-h-[300px] overflow-y-auto custom-scrollbar">
              {languages.map((lang) => (
                <DropdownMenuItem 
                  key={lang.code} 
                  onClick={() => setLanguage(lang.code)}
                  className={`flex items-center gap-3 cursor-pointer rounded-lg px-3 py-2.5 transition-all ${language === lang.code ? 'bg-blue-600 text-white shadow-md' : 'focus:bg-white/10 text-slate-300 focus:text-white'}`}
                >
                  <span className="text-lg leading-none">{lang.flag}</span>
                  <span className="text-xs font-bold">{lang.name}</span>
                </DropdownMenuItem>
              ))}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* 3. NOTIFICATIONS */}
      <Button variant="ghost" className="w-10 h-10 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 text-slate-300 hover:text-white relative transition-all active:scale-95 group">
        <Bell size={18} className="group-hover:rotate-12 transition-transform" />
        <span className="absolute top-2.5 right-3 w-1.5 h-1.5 bg-orange-500 rounded-full shadow-[0_0_8px_#f97316] animate-pulse"></span>
      </Button>

      {/* 4. USER PROFILE */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-10 pl-2 pr-3 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 text-white gap-3 transition-all group active:scale-95 ml-1">
            <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center text-xs font-bold shadow-lg text-white ring-2 ring-white/10 group-hover:ring-white/20 transition-all">
                {user?.name?.charAt(0).toUpperCase() || 'U'}
            </div>
            <div className="text-left hidden md:block">
                <span className="block text-[10px] font-bold leading-none text-slate-200 group-hover:text-white transition-colors">{user?.name?.split(' ')[0] || 'User'}</span>
            </div>
            <ChevronDown size={12} className="opacity-50 group-hover:translate-y-0.5 transition-transform" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="bg-[#111827]/95 backdrop-blur-xl border-white/10 text-white rounded-xl w-56 shadow-2xl shadow-black/50 p-1.5 animate-in fade-in zoom-in-95 duration-200">
          <div className="px-3 py-3 border-b border-white/5 mb-1 bg-white/[0.02] rounded-t-lg">
             <p className="text-xs font-bold text-white truncate">{user?.name}</p>
             <p className="text-[10px] text-slate-500 truncate mt-0.5 font-medium">{user?.email}</p>
          </div>
          
          <div className="py-1">
              <DropdownMenuItem onClick={() => navigate('/profile')} className="cursor-pointer focus:bg-blue-600/10 focus:text-blue-400 gap-2.5 rounded-lg text-xs font-medium text-slate-300 py-2.5 focus:text-white">
                <User size={14} /> {t('menu_profile') || 'My Profile'}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/settings')} className="cursor-pointer focus:bg-blue-600/10 focus:text-blue-400 gap-2.5 rounded-lg text-xs font-medium text-slate-300 py-2.5 focus:text-white">
                <Settings size={14} /> {t('menu_settings') || 'Settings'}
              </DropdownMenuItem>
          </div>

          <DropdownMenuSeparator className="bg-white/10 my-1" />
          
          <DropdownMenuItem onClick={logout} className="cursor-pointer focus:bg-red-500/10 text-red-400 focus:text-red-300 gap-2.5 rounded-lg text-xs font-bold py-2.5 group">
            <LogOut size={14} className="group-hover:-translate-x-0.5 transition-transform"/> {t('menu_logout') || 'Log Out'}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

    </div>
  );
};

export default HeaderActions;